'use client'

import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams, useRouter } from 'next/navigation'
import Link from 'next/link'
import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import {
  Globe,
  Send,
  Twitter,
  Github,
  Linkedin,
  Instagram,
  Youtube,
  Link as LinkIcon,
  UserX,
  User,
  MessageSquare,
  ArrowUp,
  Package,
  Loader2,
} from 'lucide-react'

import { Header } from '@/components/layout/header'
import { Footer } from '@/components/layout/footer'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  getUserComments,
  getUserVotes,
  getUserProducts,
  type PublicProfile,
  type SocialLinkType,
  type UserCommentItem,
  type UserVotedProductItem,
  type UserProductItem,
  socialLinkLabels,
} from '@/lib/services/profileService'

// ============================================================================
// CONFIG
// ============================================================================

const SOCIAL_ICON_MAP: Record<SocialLinkType, LucideIcon> = {
  website: Globe,
  telegram: Send,
  twitter: Twitter,
  github: Github,
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
  other: LinkIcon,
}

const SOCIAL_BADGE: Record<SocialLinkType, string> = {
  website: 'bg-neutral-600 dark:bg-neutral-400',
  telegram: 'bg-[#26A5E4]',
  twitter: 'bg-[#1DA1F2]',
  github: 'bg-[#24292f] dark:bg-[#6e7681]',
  linkedin: 'bg-[#0A66C2]',
  instagram: 'bg-[#E4405F]',
  youtube: 'bg-[#FF0000]',
  other: 'bg-neutral-600 dark:bg-neutral-400',
}

type ProfileTab = 'about' | 'forums' | 'upvotes' | 'hunted'

const TAB_META: { id: ProfileTab; label: string }[] = [
  { id: 'about', label: 'Описание' },
  { id: 'forums', label: 'Обсуждения' },
  { id: 'upvotes', label: 'Голоса' },
  { id: 'hunted', label: 'Продукты' },
]

const TAB_EMPTY: Record<ProfileTab, { icon: LucideIcon; message: string }> = {
  about: { icon: User, message: 'Информация скоро появится' },
  forums: { icon: MessageSquare, message: 'Нет обсуждений' },
  upvotes: { icon: ArrowUp, message: 'Нет голосов' },
  hunted: { icon: Package, message: 'Нет продуктов' },
}

interface TabData {
  comments: UserCommentItem[] | null
  votes: UserVotedProductItem[] | null
  products: UserProductItem[] | null
}

// ============================================================================
// NOT FOUND
// ============================================================================

function ProfileNotFound() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <UserX strokeWidth={1.5} className="w-10 h-10 text-muted-foreground/25 mx-auto mb-4" />
          <p className="text-sm font-medium text-foreground mb-1">
            Пользователь не найден
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Профиль не существует или был удалён.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link href="/">На главную</Link>
          </Button>
        </motion.div>
      </main>
      <Footer />
    </div>
  )
}

// ============================================================================
// PAGE
// ============================================================================

const VALID_TABS = new Set<ProfileTab>(['about', 'forums', 'upvotes', 'hunted'])

function parseTab(value: string | null): ProfileTab {
  if (value && VALID_TABS.has(value as ProfileTab)) return value as ProfileTab
  return 'about'
}

export default function PublicProfileClient({ initialProfile }: { initialProfile?: PublicProfile | null }) {
  const params = useParams()
  const searchParams = useSearchParams()
  const router = useRouter()
  const username = params.username as string

  // The profile is fully public and server-rendered by the page wrapper, so it
  // comes straight from props — no client fetch or loading state. Reading the
  // prop directly (rather than seeding state) keeps it correct across
  // client-side navigation between profiles.
  const profile = initialProfile ?? null

  // Tab data (lazy-loaded, cached in state)
  const [tabData, setTabData] = useState<TabData>({
    comments: null,
    votes: null,
    products: null,
  })
  const [tabLoading, setTabLoading] = useState<Partial<Record<ProfileTab, boolean>>>({})

  const activeTab = parseTab(searchParams.get('tab'))

  const setActiveTab = useCallback((tab: ProfileTab) => {
    const params = new URLSearchParams(searchParams.toString())
    if (tab === 'about') {
      params.delete('tab')
    } else {
      params.set('tab', tab)
    }
    const query = params.toString()
    router.replace(`/u/${username}${query ? `?${query}` : ''}`, { scroll: false })
  }, [searchParams, router, username])

  // Lazy-load tab data on first visit
  useEffect(() => {
    if (!profile || !username) return

    if (activeTab === 'forums' && tabData.comments === null && !tabLoading.forums) {
      setTabLoading(prev => ({ ...prev, forums: true }))
      getUserComments(username)
        .then(res => setTabData(prev => ({ ...prev, comments: res.comments })))
        .catch(() => setTabData(prev => ({ ...prev, comments: [] })))
        .finally(() => setTabLoading(prev => ({ ...prev, forums: false })))
    }

    if (activeTab === 'upvotes' && tabData.votes === null && !tabLoading.upvotes) {
      setTabLoading(prev => ({ ...prev, upvotes: true }))
      getUserVotes(username)
        .then(res => setTabData(prev => ({ ...prev, votes: res.products })))
        .catch(() => setTabData(prev => ({ ...prev, votes: [] })))
        .finally(() => setTabLoading(prev => ({ ...prev, upvotes: false })))
    }

    if (activeTab === 'hunted' && tabData.products === null && !tabLoading.hunted) {
      setTabLoading(prev => ({ ...prev, hunted: true }))
      getUserProducts(username)
        .then(res => setTabData(prev => ({ ...prev, products: res.products })))
        .catch(() => setTabData(prev => ({ ...prev, products: [] })))
        .finally(() => setTabLoading(prev => ({ ...prev, hunted: false })))
    }
  }, [activeTab, profile, username, tabData, tabLoading])

  if (!profile) return <ProfileNotFound />

  const initials = profile.name?.substring(0, 2).toUpperCase() || 'U'
  const hasSocials = profile.socialLinks?.length > 0
  const isTabLoading = !!tabLoading[activeTab]

  // Tab counts from profile response
  const tabCounts: Partial<Record<ProfileTab, number>> = {
    forums: profile.commentCount,
    upvotes: profile.voteCount,
    hunted: profile.productCount,
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="max-w-3xl mx-auto px-4 md:px-6 pt-6 md:pt-10 pb-12 md:pb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {/* Identity */}
            <div className="col-span-2 md:col-span-4 border border-border rounded-2xl p-8 bg-card flex items-center gap-6">
              <Avatar className="h-24 w-24 md:h-28 md:w-28 shrink-0">
                <AvatarImage src={profile.avatarUrl || undefined} alt={profile.name} />
                <AvatarFallback className="text-2xl font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                  {profile.name}
                </h1>
                {profile.headline && (
                  <p className="text-[15px] text-muted-foreground mt-1">
                    {profile.headline}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-0.5">
                  @{profile.username}
                </p>
              </div>
            </div>

            {/* Social links */}
            {hasSocials &&
              profile.socialLinks.map((link, i) => {
                const Icon = SOCIAL_ICON_MAP[link.type]
                const badge = SOCIAL_BADGE[link.type]
                const label = link.label || socialLinkLabels[link.type]
                return (
                  <a
                    key={`${link.type}-${i}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="col-span-1 border border-border rounded-xl px-4 py-3.5 bg-card flex items-center gap-2.5 text-sm text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg shrink-0 ${badge}`}>
                      <Icon className="h-3.5 w-3.5 text-white dark:text-white" />
                    </span>
                    <span className="truncate">{label}</span>
                  </a>
                )
              })}

            {/* Tabs */}
            <div className="col-span-2 md:col-span-4 border border-border rounded-2xl bg-card p-1.5 flex gap-0.5 overflow-x-auto">
              {TAB_META.map((tab) => {
                const count = tabCounts[tab.id]
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3.5 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {tab.label}
                    {count !== undefined && count > 0 && (
                      <span
                        className={`ml-1.5 tabular-nums ${
                          activeTab === tab.id
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/50'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Tab content */}
            <div className="col-span-2 md:col-span-4 border border-border rounded-2xl bg-card p-8">
              <TabContent
                activeTab={activeTab}
                profile={profile}
                tabData={tabData}
                isLoading={isTabLoading}
              />
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  )
}

// ============================================================================
// TAB CONTENT
// ============================================================================

function TabContent({
  activeTab,
  profile,
  tabData,
  isLoading,
}: {
  activeTab: ProfileTab
  profile: PublicProfile
  tabData: TabData
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
      </div>
    )
  }

  // About tab
  if (activeTab === 'about') {
    if (profile.bio) {
      return (
        <p className="text-[15px] text-foreground leading-relaxed whitespace-pre-wrap">
          {profile.bio}
        </p>
      )
    }
    return <TabEmptyState tab="about" />
  }

  // Forums tab
  if (activeTab === 'forums') {
    const comments = tabData.comments
    if (!comments || comments.length === 0) return <TabEmptyState tab="forums" />
    return (
      <div className="space-y-1">
        {comments.map((comment) => (
          <Link
            key={comment.id}
            href={`/products/${comment.productSlug}#comments`}
            className="flex items-start gap-3 p-3 -mx-3 rounded-xl hover:bg-muted/50 transition-colors"
          >
            {comment.productLogoUrl ? (
              <img
                src={comment.productLogoUrl}
                alt={comment.productName}
                className="h-8 w-8 rounded-lg shrink-0 object-cover"
              />
            ) : (
              <div className="h-8 w-8 rounded-lg shrink-0 bg-muted flex items-center justify-center">
                <Package className="h-4 w-4 text-muted-foreground/40" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm mb-1">
                <span className="font-medium text-foreground">{comment.productName}</span>
                {comment.productTagline && (
                  <span className="text-muted-foreground"> — {comment.productTagline}</span>
                )}
              </p>
              <p className="text-sm text-foreground line-clamp-2">{comment.content}</p>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-xs text-muted-foreground/60">
                  {new Date(comment.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                </span>
                {comment.voteCount !== 0 && (
                  <span className="text-xs text-muted-foreground/60 flex items-center gap-0.5">
                    <ArrowUp className="h-3 w-3" />
                    {comment.voteCount}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    )
  }

  // Upvotes tab
  if (activeTab === 'upvotes') {
    const votes = tabData.votes
    if (!votes || votes.length === 0) return <TabEmptyState tab="upvotes" />
    return (
      <div className="space-y-1">
        {votes.map((product) => (
          <Link
            key={product.id}
            href={`/products/${product.slug}`}
            className="flex items-center gap-3 p-3 -mx-3 rounded-xl hover:bg-muted/50 transition-colors"
          >
            <img
              src={product.logoUrl}
              alt={product.name}
              className="h-10 w-10 rounded-xl shrink-0 object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
              <p className="text-xs text-muted-foreground truncate">{product.tagline}</p>
            </div>
            <div className="text-xs text-muted-foreground/60 tabular-nums flex items-center gap-0.5 shrink-0">
              <ArrowUp className="h-3 w-3" />
              {product.voteCount}
            </div>
          </Link>
        ))}
      </div>
    )
  }

  // Hunted tab
  if (activeTab === 'hunted') {
    const products = tabData.products
    if (!products || products.length === 0) return <TabEmptyState tab="hunted" />
    return (
      <div className="space-y-1">
        {products.map((product) => (
          <Link
            key={product.id}
            href={`/products/${product.slug}`}
            className="flex items-center gap-3 p-3 -mx-3 rounded-xl hover:bg-muted/50 transition-colors"
          >
            <img
              src={product.logoUrl}
              alt={product.name}
              className="h-10 w-10 rounded-xl shrink-0 object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
              <p className="text-xs text-muted-foreground truncate">{product.tagline}</p>
            </div>
            <div className="text-xs text-muted-foreground/60 tabular-nums flex items-center gap-0.5 shrink-0">
              <ArrowUp className="h-3 w-3" />
              {product.voteCount}
            </div>
          </Link>
        ))}
      </div>
    )
  }

  return <TabEmptyState tab={activeTab} />
}

function TabEmptyState({ tab }: { tab: ProfileTab }) {
  const { icon: TabIcon, message } = TAB_EMPTY[tab]
  return (
    <div className="py-10 text-center">
      <TabIcon
        strokeWidth={1.5}
        className="h-5 w-5 text-muted-foreground/20 mx-auto mb-2.5"
      />
      <p className="text-sm text-muted-foreground/50">{message}</p>
    </div>
  )
}
